import { z } from 'zod'

export const LEARNING_GRANTS_WRITE_CONTRACT_VERSION = '1.0.0' as const
export const LEARNING_GRANTS_SCHEMA_VERSION = 'learning-grants-v1' as const

const nullableString = z.string().nullable()
const schemaVersion = z.object({ schemaVersion: z.literal(LEARNING_GRANTS_SCHEMA_VERSION) })
const requiredText = z.string().trim().min(1).max(240)
const nullableText = z.string().trim().min(1).max(240).nullable().optional()
const nullableTimestamp = z.string().trim().min(1).max(80).nullable().optional()
const requiredReason = z.string().trim().min(1).max(1000)

const grantRuleSchema = z.object({
  id: z.string().min(1),
  membershipKey: z.string().min(1),
  programKey: nullableString,
  catalogNodeId: z.string().min(1),
  grantPolicy: z.enum(['grant_node_only', 'grant_descendants', 'grant_bundle_courses', 'manual_review']),
  status: z.enum(['draft', 'active', 'inactive']),
  effectiveFrom: nullableString,
  effectiveTo: nullableString,
  createdBy: nullableString,
  updatedBy: nullableString,
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

const entitlementGrantSchema = z.object({
  id: z.string().min(1),
  memberProfileId: z.string().min(1),
  catalogNodeId: z.string().min(1),
  grantRuleId: nullableString,
  grantSource: z.enum(['rule', 'vjal_import', 'manual_grant', 'migration', 'support_fix']),
  status: z.enum(['active', 'pending', 'expired', 'revoked', 'paused', 'overridden', 'manual_review']),
  sourceRef: z.string().min(1),
  effectiveFrom: nullableString,
  expiresAt: nullableString,
  closedAt: nullableString,
  closedReason: nullableString,
  reason: nullableString,
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

const projectedGrantSchema = z.object({
  memberProfileId: z.string().min(1),
  catalogNodeId: z.string().min(1),
  grantRuleId: z.string().min(1),
  grantSource: z.literal('rule'),
  sourceRef: z.string().min(1),
  effectiveFrom: nullableString,
  expiresAt: nullableString,
  status: z.enum(['active', 'manual_review']),
}).passthrough()

const grantRulePreviewSchema = z.object({
  rule: grantRuleSchema,
  projectedGrants: z.array(projectedGrantSchema),
  total: z.number().int().nonnegative(),
}).passthrough()

export const learningGrantRuleCreateRequestSchema = schemaVersion.extend({
  membershipKey: requiredText,
  programKey: nullableText,
  catalogNodeId: requiredText,
  grantPolicy: z.enum(['grant_node_only', 'grant_descendants', 'grant_bundle_courses', 'manual_review']),
  effectiveFrom: nullableTimestamp,
  effectiveTo: nullableTimestamp,
}).strict()

export const learningGrantRuleUpdateRequestSchema = schemaVersion.extend({
  membershipKey: requiredText.optional(),
  programKey: nullableText,
  catalogNodeId: requiredText.optional(),
  grantPolicy: z.enum(['grant_node_only', 'grant_descendants', 'grant_bundle_courses', 'manual_review']).optional(),
  effectiveFrom: nullableTimestamp,
  effectiveTo: nullableTimestamp,
}).strict().refine((value) => Object.keys(value).some((key) => key !== 'schemaVersion'), {
  message: 'At least one grant-rule field is required.',
})

export const learningGrantRuleActionRequestSchema = z.object({}).passthrough()

export const learningManualGrantCreateRequestSchema = schemaVersion.extend({
  memberProfileId: requiredText,
  catalogNodeId: requiredText,
  reason: requiredReason,
  expiresAt: nullableTimestamp,
}).strict()

export const learningGrantRevokeRequestSchema = schemaVersion.extend({
  reason: requiredReason,
}).strict()

export const learningGrantRecomputeRequestSchema = z.object({
  scope: z.enum(['member', 'membership_key', 'all']).default('all'),
  memberProfileId: z.string().trim().min(1).max(180).optional(),
  membershipKey: z.string().trim().min(1).max(240).optional(),
  dryRun: z.boolean().default(false),
}).strict().superRefine((value, ctx) => {
  if (value.scope === 'member' && !value.memberProfileId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'memberProfileId is required for member scope.', path: ['memberProfileId'] })
  }
  if (value.scope === 'membership_key' && !value.membershipKey) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'membershipKey is required for membership_key scope.', path: ['membershipKey'] })
  }
})

const itemResponse = <T extends z.ZodTypeAny>(item: T) => z.object({
  contract_version: z.literal(LEARNING_GRANTS_WRITE_CONTRACT_VERSION),
  item,
}).passthrough()

export const learningGrantRuleCreateResponseSchema = itemResponse(grantRuleSchema)
export const learningGrantRuleUpdateResponseSchema = itemResponse(grantRuleSchema)
export const learningGrantRuleActivateResponseSchema = itemResponse(grantRuleSchema)
export const learningGrantRuleDeactivateResponseSchema = itemResponse(grantRuleSchema)
export const learningGrantRulePreviewResponseSchema = itemResponse(grantRulePreviewSchema)
export const learningManualGrantCreateResponseSchema = itemResponse(entitlementGrantSchema)
export const learningGrantRevokeResponseSchema = itemResponse(entitlementGrantSchema)
export const learningGrantRecomputeResponseEnvelopeSchema = z.object({
  contract_version: z.literal(LEARNING_GRANTS_WRITE_CONTRACT_VERSION),
  scope: z.enum(['member', 'membership_key', 'all']),
  dryRun: z.boolean(),
  created: z.number().int().nonnegative(),
  closed: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
}).passthrough()

export type LearningGrantRuleCreateRequest = z.infer<typeof learningGrantRuleCreateRequestSchema>
export type LearningGrantRuleUpdateRequest = z.infer<typeof learningGrantRuleUpdateRequestSchema>
export type LearningGrantRuleActionRequest = z.infer<typeof learningGrantRuleActionRequestSchema>
export type LearningManualGrantCreateRequest = z.infer<typeof learningManualGrantCreateRequestSchema>
export type LearningGrantRevokeRequest = z.infer<typeof learningGrantRevokeRequestSchema>
export type LearningGrantRecomputeRequest = z.infer<typeof learningGrantRecomputeRequestSchema>
export type LearningGrantRuleCreateResponse = z.infer<typeof learningGrantRuleCreateResponseSchema>
export type LearningGrantRuleUpdateResponse = z.infer<typeof learningGrantRuleUpdateResponseSchema>
export type LearningGrantRuleActivateResponse = z.infer<typeof learningGrantRuleActivateResponseSchema>
export type LearningGrantRuleDeactivateResponse = z.infer<typeof learningGrantRuleDeactivateResponseSchema>
export type LearningGrantRulePreviewResponse = z.infer<typeof learningGrantRulePreviewResponseSchema>
export type LearningManualGrantCreateResponse = z.infer<typeof learningManualGrantCreateResponseSchema>
export type LearningGrantRevokeResponse = z.infer<typeof learningGrantRevokeResponseSchema>
export type LearningGrantRecomputeResponse = z.infer<typeof learningGrantRecomputeResponseEnvelopeSchema>
